import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchDancerVisibility,
  setDancerVisibility,
  type DancerVisibility,
} from '../api/portal'

/**
 * Who on this account the family wants to see (D-381).
 *
 * **Real households carry rows that dance nothing.** An adult who is also a
 * guardian and once took parent numbers; a friend's child signed up under the
 * account one season; a sibling who has graduated. 24 of the 164 families with
 * a dancer enrolled carry at least one, and each costs a card on the portal
 * that opens onto an empty screen.
 *
 * **A switch rather than a rule.** 18 of the 29 such rows in the real data are
 * ordinary-age children, and until a season starts *every* dancer in the studio
 * has nothing on — so hiding on "no enrolments" would empty the portal every
 * September. The count is shown to the person deciding; the decision is theirs.
 *
 * **It flips both ways, because the answer changes.** Becky danced when the
 * studio ran parent numbers and may again; Emilia was a friend's child and is
 * not any more.
 */
export default function DancerVisibilityPanel() {
  const queryClient = useQueryClient()

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['dancer-visibility'],
    queryFn: fetchDancerVisibility,
  })

  const [confirming, setConfirming] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: ({ dancerId, visible }: { dancerId: string; visible: boolean }) =>
      setDancerVisibility(dancerId, visible),

    onSuccess: () => {
      setConfirming(null)
      queryClient.invalidateQueries({ queryKey: ['dancer-visibility'] })

      /*
       * Everything scoped to the family changes with this, not just Home — the
       * schedule and the calendar read the same resolver. Invalidating only
       * the portal would leave a hidden dancer on the calendar until it
       * happened to refetch.
       */
      for (const key of [['portal'], ['portal-home'], ['calendar'], ['schedule']]) {
        queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })

  /*
   * Nothing to choose between with one dancer, and a switch that can only hide
   * your only child is a control that exists to cause support calls.
   *
   * **After the error branch, not before it.** This ran first, so a failed load
   * — `isPending` false, `data` undefined — returned null and the alert below
   * was unreachable. Every database-backed endpoint refuses for ~75 seconds
   * after a deploy (D-33), which made "the panel is silently missing" the
   * ordinary post-deploy experience rather than an edge case.
   */
  if (!isPending && !isError && (data?.length ?? 0) < 2) return null

  /*
   * The last one standing cannot be switched off (D-381).
   *
   * Hiding everybody empties the scope, and `/api/portal` then answers with the
   * no-dancers state: *"We could not find any dancers on your account. Please
   * get in touch with the studio."* That sentence is false and it spends the
   * one resource this project exists to protect — on something the parent did
   * to herself two taps earlier. Refusing is smaller than explaining.
   */
  const visible = data?.filter((d) => d.visible).length ?? 0

  return (
    <section className="mt-8">
      <h2 className="mb-1.5 text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
        Who you see
      </h2>

      <p className="mb-3 text-[13px] text-ink-3">
        Turn someone off and they stop appearing on your home screen and schedule. Nothing is
        deleted &mdash; the studio still has them, and their attendance and history are
        untouched.
      </p>

      {isPending && <p className="text-[13px] text-ink-3">Loading...</p>}

      {isError && (
        <p role="alert" className="text-[13px] text-crit">
          {(error as Error).message}
        </p>
      )}

      {save.error && (
        <p role="alert" className="mb-2 text-[13px] text-crit">
          {(save.error as Error).message}
        </p>
      )}

      <div className="rounded-2xl border border-line bg-surface px-4">
        {data?.map((dancer) => (
          <Row
            key={dancer.dancerId}
            dancer={dancer}
            busy={save.isPending}
            isLastVisible={dancer.visible && visible === 1}
            confirming={confirming === dancer.dancerId}
            onAsk={() => setConfirming(dancer.dancerId)}
            onCancel={() => setConfirming(null)}
            onSet={(visible) => save.mutate({ dancerId: dancer.dancerId, visible })}
          />
        ))}
      </div>
    </section>
  )
}

function Row({
  dancer,
  busy,
  isLastVisible,
  confirming,
  onAsk,
  onCancel,
  onSet,
}: {
  dancer: DancerVisibility
  busy: boolean
  /** Switching this one off would empty the portal, so it is refused. */
  isLastVisible: boolean
  confirming: boolean
  onAsk: () => void
  onCancel: () => void
  onSet: (visible: boolean) => void
}) {
  /*
   * Hiding somebody who is actually dancing is allowed and asks first. It is
   * how a parent loses a Tuesday — and the studio has no way to know she did
   * it by accident, because from the outside it looks like a deliberate
   * setting.
   */
  const needsAsking = dancer.visible && dancer.classes > 0

  const toggle = () => {
    if (isLastVisible) return
    if (needsAsking && !confirming) onAsk()
    else onSet(!dancer.visible)
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-line py-3 first:border-t-0">
      <div className={dancer.visible ? '' : 'opacity-60'}>
        <p className="text-[14px] font-semibold">{dancer.name}</p>
        <p className="text-[12.5px] text-ink-3">
          {isLastVisible
            ? 'The only one showing — turn someone else on first'
            : dancer.classes === 0
              ? 'Not in any classes right now'
              : `${dancer.classes} ${dancer.classes === 1 ? 'class' : 'classes'} this season`}
        </p>
      </div>

      <button
        type="button"
        role="switch"
        aria-checked={dancer.visible}
        aria-label={`Show ${dancer.name}`}
        disabled={busy || isLastVisible}
        onClick={toggle}
        className={`relative ml-auto h-[26px] w-[46px] shrink-0 rounded-full transition-colors disabled:opacity-50 ${
          dancer.visible ? 'bg-good' : 'bg-line-2'
        }`}
      >
        <span
          className={`absolute top-[3px] left-[3px] h-5 w-5 rounded-full bg-white transition-transform ${
            dancer.visible ? 'translate-x-5' : ''
          }`}
        />
      </button>

      {confirming && (
        <div className="w-full rounded-xl border border-warn bg-warn-bg px-3 py-2.5">
          <p className="text-[13px]">
            {dancer.name} is in{' '}
            {dancer.classes === 1 ? 'a class' : `${dancer.classes} classes`} this season. Hide her
            anyway?
          </p>

          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onSet(false)}
              disabled={busy}
              className="rounded-lg bg-accent px-3 py-1.5 text-[12.5px] font-semibold text-on-accent disabled:opacity-50"
            >
              Hide her
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
            >
              Keep her
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
